import { useCallback, useEffect, useMemo, RefObject } from 'react';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { useAssetViewerState } from '@hooks/useAssetViewerState';

export const PANEL_OVERLAY_SELECTOR = [
  '.media_viewer_menu',
  '.panel-section_head-overlay',
  '.media_viewer_tooltip',
  '.comment_mentions',
  '.ant-dropdown',
  '.ant-select-dropdown',
  '.ant-picker-dropdown',
  '.compare_version_tools_container',
  '.compare-versions-modal_head',
  '.app-tour-assist',
  '.app-tour-highlighted-mask',
  '.shapes_drawing_tools_container',
  '.video_control_bar',
  '.media_viewer_panel-action',
  '.media_viewer_sidebar',
  '.media_viewer_sidebar-menu',
  '.media_viewer_sidebar-menu-btn',
  '.media_viewer_sidebar-content',
  '.panel-section',
  '.media_viewer_pdf-sidebar',
  '.media_viewer_pdf-actions',
  '.task-modal',
  '.reactour__mask',
  '.reactour__helper',
  '.reactour__close'
].join(', ');

type SidebarPanelDismissHandlerProps = {
  sidebarRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  isPinned: boolean;
  isLgWidth: boolean;
  onClose(): void;
};

function SidebarPanelDismissHandler({
  sidebarRef,
  isOpen,
  isPinned,
  isLgWidth,
  onClose
}: SidebarPanelDismissHandlerProps) {
  const {
    isCommentFieldFocused,
    commentText,
    canvasMode,
    selectedCommentId,
    playerTime,
    documentHighlight
  } = useAssetViewerState();

  const canDismissSidebarPanel = useMemo(() => {
    if (isCommentFieldFocused) return false;
    if (commentText?.trim()) return false;
    if (canvasMode === 'edit') return false;
    if (selectedCommentId === 'new' || selectedCommentId === 'reply') {
      return false;
    }
    if (playerTime[1] != null) return false;
    if (documentHighlight?.text?.trim()) return false;
    return true;
  }, [
    isCommentFieldFocused,
    commentText,
    canvasMode,
    selectedCommentId,
    playerTime,
    documentHighlight
  ]);

  const closePanelIfAllowed = useCallback(() => {
    if (!isOpen || isPinned || !isLgWidth || !canDismissSidebarPanel) return;
    onClose();
  }, [isOpen, isPinned, isLgWidth, canDismissSidebarPanel, onClose]);

  useEffect(() => {
    if (!isLgWidth || !isOpen || isPinned) return undefined;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePanelIfAllowed();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isLgWidth, isOpen, isPinned, closePanelIfAllowed]);

  useOnClickOutside(sidebarRef, (e) => {
    if (!isOpen || isPinned || !isLgWidth) return;
    if (e.target.closest(PANEL_OVERLAY_SELECTOR)) return;
    closePanelIfAllowed();
  });

  return null;
}

export default SidebarPanelDismissHandler;
